import { Box, Button, Stack, useTheme } from "@mui/material";
import { useAtomValue, useSetAtom } from "jotai";
import { ArrowLeft, RotateCcw } from "lucide-react";
import type { FC } from "react";
import {
	Navigate,
	useLocation,
	useNavigate,
	useSearchParams,
} from "react-router-dom";
import { useNotificationCenterRightOptional } from "web_myplann/Api/Rights/RightsProvider";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import {
	DEFAULT_TIMELINE_FILTER,
	isTimelineFilterDefaultAtom,
	timelineFilterAtom,
} from "web_myplann/NotificationCenter/Atoms/timelineFilterAtom";
import {
	DEFAULT_NOTIFICATION_CENTER_TAB,
	isNotificationCenterTab,
	NOTIFICATION_CENTER_TAB_QUERY_PARAM,
	NOTIFICATION_CENTER_TABS,
	type NotificationCenterTab,
} from "web_myplann/NotificationCenter/Constants/notificationCenterTabs";
import { RecentFilters } from "web_myplann/NotificationCenter/FilterScreen/RecentFilters";
import { UpcomingFilters } from "web_myplann/NotificationCenter/FilterScreen/UpcomingFilters";
import { HeaderIconButton } from "web_myplann/Shared/Header/HeaderIconButton";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { rightsState } from "web_myplann/Shared/State/rightsAtom";

export const FilterScreenPage: FC = () => {
	const { t } = useResource();
	const theme = useTheme();
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const isRightsPending = useAtomValue(rightsState) === undefined;
	const hasNotificationCenterRight = useNotificationCenterRightOptional();

	const urlTab = searchParams.get(NOTIFICATION_CENTER_TAB_QUERY_PARAM);
	const activeTab: NotificationCenterTab = isNotificationCenterTab(urlTab)
		? urlTab
		: DEFAULT_NOTIFICATION_CENTER_TAB;

	const isDefault = useAtomValue(isTimelineFilterDefaultAtom);
	const setFilter = useSetAtom(timelineFilterAtom);

	const backRoute = Routing.Notifications.path(activeTab);

	const handleBack = () => {
		if (location.key !== "default") {
			navigate(-1);
			return;
		}
		navigate(backRoute, { replace: true });
	};

	if (isRightsPending) {
		return null;
	}

	if (!hasNotificationCenterRight) {
		return <Navigate to={Routing.Notifications.path()} replace />;
	}

	return (
		<MyPlannLayout
			disableContentPadding
			topBar={
				<NavigationHeader
					title={t(Resources.NotificationCenter.FilterScreen.Title)}
					startComponent={
						<HeaderIconButton
							onClick={handleBack}
							aria-label={t(Resources.NotificationCenter.FilterScreen.Title)}
							data-testid="notification-center-filter-back"
						>
							<ArrowLeft color={theme.palette.primary.main} />
						</HeaderIconButton>
					}
					endComponent={
						isDefault ? null : (
							<HeaderIconButton
								onClick={() => setFilter(DEFAULT_TIMELINE_FILTER)}
								aria-label={t(
									Resources.NotificationCenter.FilterScreen.ResetAriaLabel,
								)}
								data-testid="notification-center-filter-reset"
							>
								<RotateCcw color={theme.palette.primary.main} />
							</HeaderIconButton>
						)
					}
				/>
			}
		>
			<Stack height="100%" data-testid="notification-center-filter-screen">
				<Stack flex={1} sx={{ overflowY: "auto" }}>
					{activeTab === NOTIFICATION_CENTER_TABS.Upcoming ? (
						<UpcomingFilters />
					) : (
						<RecentFilters />
					)}
				</Stack>

				<Box
					sx={{
						position: "sticky",
						bottom: 0,
						backgroundColor: theme.palette.common.white,
						px: 1.6,
						py: 1.6,
						borderTop: `1px solid ${theme.palette.grey[100]}`,
					}}
				>
					<Button
						variant="contained"
						color="secondary"
						onClick={handleBack}
						fullWidth
						data-testid="notification-center-filter-apply"
						sx={{
							borderRadius: 99,
							py: 1.4,
							fontSize: 15,
							fontWeight: 600,
							textTransform: "none",
						}}
					>
						{t(Resources.NotificationCenter.FilterScreen.Apply)}
					</Button>
				</Box>
			</Stack>
		</MyPlannLayout>
	);
};
